import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Grid,
  Skeleton,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { PaymentMethodsIcon } from "@assets/icons/PaymentMethodsIcon";
import { FormErrorMessage } from "@shared/components/FormErrorMessage";
import { Form } from "@shared/components/Form";
import { Controller, useForm } from "react-hook-form";
import {
  AddPaymentMethodFormSchema,
  addPaymentMethodFormSchema,
} from "./add-payment-method-form-helpers";
import { yupResolver } from "@hookform/resolvers/yup";
import { TextField } from "@shared/components/TextField";
import { CountrySelect } from "@shared/components/CountrySelect";
import { StateField } from "@shared/components/StateField";
import { getBrowserDefaultCountry } from "@shared/utils/countries";
import { SubscriptionSubmitButton } from "@features/Subscription/components/styles";
import { useTheme } from "@shared/theme/useTheme";
import { useUpdateSubscriptionPaymentMethod } from "@queries/subscription/useUpdateSubscriptionPaymentMethod";

const PaymentMethodInputWrapper = styled.div<{ error: boolean }>`
  width: 100%;
  border-radius: 0.5rem;
  border: 1px solid
    ${(p) => (p.error ? p.theme.colors.error : p.theme.input.colors.border)};
  padding: 14.5px 16px;
  background-color: ${(p) => p.theme.input.colors.background};
  transition: border-color 0.2s ease-in-out;
`;

type Props = {
  open: boolean;
  onClose: () => void;
  billingDetails: {
    address: {
      city: string;
      country: string;
      line1: string;
      line2?: string;
      zipCode: string;
      state: string;
    };
  } | null;
};

const getDefaultFormValues = (
  billingDetails: Props["billingDetails"],
): AddPaymentMethodFormSchema => ({
  addressLine1: billingDetails?.address.line1 ?? "",
  addressLine2: billingDetails?.address.line2 ?? "",
  addressCity: billingDetails?.address.city ?? "",
  addressState: billingDetails?.address.state ?? "",
  addressCountry: billingDetails?.address.country || getBrowserDefaultCountry(),
  addressZipCode: billingDetails?.address.zipCode ?? "",
});

export const AddPaymentMethodModal = (props: Props) => {
  const { open, onClose, billingDetails } = props;
  const [paymentStatus, setPaymentStatus] = useState<
    "idle" | "loading" | "error"
  >("idle");
  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    control,
    getValues,
    watch,
    reset,
  } = useForm<AddPaymentMethodFormSchema>({
    defaultValues: getDefaultFormValues(billingDetails),
    resolver: yupResolver(addPaymentMethodFormSchema),
    reValidateMode: "onChange",
  });
  const selectedCountry = watch("addressCountry");
  const theme = useTheme();
  const [paymentErrorMessage, setPaymentErrorMessage] = useState("");

  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    setTimeout(() => {
      const cardElement = elements?.getElement(CardElement);
      if (!cardElement) return;
      cardElement.focus();
    }, 1000);
  }, [elements]);

  useEffect(() => {
    setTimeout(() => {
      const cardElement = elements?.getElement(CardElement);
      if (!cardElement) return;

      cardElement.on("change", ({ error }) => {
        setPaymentErrorMessage(error ? error.message : "");
      });
    }, 1000);
  }, [elements, setValue, getValues]);

  const handleClose = useCallback(() => {
    const cardElement = elements?.getElement(CardElement);

    if (cardElement) {
      cardElement.clear();
    }

    reset(getDefaultFormValues(billingDetails));
    setPaymentErrorMessage("");
    setPaymentStatus("idle");

    onClose();
  }, [onClose, reset, billingDetails, elements]);

  const updateSubscriptionPaymentMethodMutation =
    useUpdateSubscriptionPaymentMethod();

  const onFormSubmit = async (data: AddPaymentMethodFormSchema) => {
    if (!elements || !stripe) return;

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) return;

    setPaymentStatus("loading");

    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
      billing_details: {
        address: {
          city: data.addressCity,
          line1: data.addressLine1,
          line2: data.addressLine2,
          postal_code: data.addressZipCode,
          state: data.addressState,
          country: data.addressCountry,
        },
      },
    });

    if (error && error.message) {
      setPaymentStatus("error");
      setPaymentErrorMessage(error.message);
      return;
    }

    if (!paymentMethod) {
      setPaymentStatus("error");
      setPaymentErrorMessage(
        "An error occurred while trying to add you payment method. Please try again later.",
      );
      return;
    }

    await updateSubscriptionPaymentMethodMutation.mutateAsync({
      paymentMethodId: paymentMethod.id,
    });

    handleClose();
  };

  if (!stripe || !elements) {
    return <Skeleton variant="rounded" sx={{ width: "100%", height: 480 }} />;
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Payment method</DialogTitle>
      <DialogContent>
        <Form
          onSubmit={handleSubmit(onFormSubmit)}
          aria-disabled={paymentStatus === "loading"}
          maxWidth="100%"
          id="add-payment-method-form"
        >
          <Typography fontWeight={600}>Card</Typography>
          <FormControl>
            <FormLabel>Card Information</FormLabel>
            <PaymentMethodInputWrapper error={!!paymentErrorMessage}>
              <CardElement
                id="card-element"
                options={{
                  style: {
                    base: {
                      color: theme.input.colors.primary,
                      fontFamily: theme.text.fonts.primary,
                      "::placeholder": {
                        color: theme.input.colors.placeholder,
                      },
                    },
                    invalid: {
                      color: theme.colors.error,
                      iconColor: theme.colors.error,
                    },
                  },
                  hidePostalCode: true,
                  disableLink: true,
                }}
              />
            </PaymentMethodInputWrapper>
            <FormErrorMessage>{paymentErrorMessage}</FormErrorMessage>
            <Typography mt={1}>
              <PaymentMethodsIcon />
            </Typography>
          </FormControl>
          <Typography fontWeight={600}>Billing address</Typography>
          <Grid container columnSpacing={2} rowSpacing={1}>
            <Grid item sm={6} xs={12}>
              <TextField
                label="Address"
                name="addressLine1"
                placeholder="Address"
                inputProps={{ ...register("addressLine1") }}
                fieldError={errors.addressLine1}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <TextField
                label="Address line 2"
                name="addressLine2"
                placeholder="Address line 2"
                inputProps={{ ...register("addressLine2") }}
                fieldError={errors.addressLine2}
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <TextField
                label="City"
                name="addressCity"
                placeholder="City"
                inputProps={{ ...register("addressCity") }}
                fieldError={errors.addressCity}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <Controller
                name="addressState"
                control={control}
                render={({ field }) => (
                  <StateField
                    country={selectedCountry}
                    name="addressState"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    fieldError={errors.addressState}
                  />
                )}
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <TextField
                label="Postal code"
                name="addressZipCode"
                inputProps={{ ...register("addressZipCode") }}
                fieldError={errors.addressZipCode}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <Controller
                name="addressCountry"
                control={control}
                render={({ field }) => (
                  <CountrySelect
                    label="Country"
                    name="addressCountry"
                    value={field.value}
                    onChange={(newCountry) => {
                      field.onChange(newCountry);
                      setValue("addressState", "");
                    }}
                    onBlur={field.onBlur}
                    fieldError={errors.addressCountry}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Form>
      </DialogContent>
      <DialogActions>
        <Button
          variant="text"
          onClick={handleClose}
          disabled={paymentStatus === "loading"}
        >
          Cancel
        </Button>
        <SubscriptionSubmitButton
          isLoading={paymentStatus === "loading"}
          loadingText="Processing..."
          form="add-payment-method-form"
        >
          Save
        </SubscriptionSubmitButton>
      </DialogActions>
    </Dialog>
  );
};
